'use client';

// BotHeader - Header component with bot name, description, and Boty icon
// Story 4.1: Access Bot via Public Link
// Story 4.5: Mobile-Optimized Bot Interface
// Story 17.1: WhatsApp deep link button

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import { useTranslations } from 'next-intl';
import { BotyIcon } from '@boty/ui';

interface BotHeaderProps {
  name: string;
  slug: string;
  description?: string | null;
}

export function BotHeader({ name, slug, description }: BotHeaderProps) {
  const t = useTranslations('consumer.bot');
  const whatsappNumber = process.env.NEXT_PUBLIC_WHATSAPP_PHONE_NUMBER;
  const whatsappLink = whatsappNumber
    ? `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(`Chat with Boty: ${name} (${slug})`)}`
    : null;

  return (
    <Box
      component="header"
      sx={{
        bgcolor: 'primary.main', // Indigo #4f46e5
        color: 'white',
        // Responsive padding: more compact on mobile
        py: { xs: 1.5, sm: 2, md: 3 },
        px: { xs: 1.5, sm: 2 },
        textAlign: 'center',
        position: 'relative',
      }}
    >
      {/* WhatsApp button - top right corner */}
      {whatsappLink && (
        <Tooltip title={t('chatOnWhatsApp')}>
          <IconButton
            component="a"
            href={whatsappLink}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              position: 'absolute',
              top: { xs: 8, sm: 12 },
              right: { xs: 8, sm: 12 },
              color: 'white',
              bgcolor: 'rgba(37, 211, 102, 0.2)',
              '&:hover': { bgcolor: 'rgba(37, 211, 102, 0.4)' },
            }}
            size="small"
          >
            <WhatsAppIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}

      {/* Boty icon - with white circular background for visibility on purple header */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          mb: 1,
        }}
      >
        <Box
          sx={{
            bgcolor: 'white',
            borderRadius: '50%',
            p: 0.75,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <BotyIcon size={36} variant="simple" />
        </Box>
      </Box>

      <Typography
        variant="h5"
        component="h1"
        fontWeight={600}
        sx={{
          mb: description ? 0.5 : 0,
          // Responsive font size for mobile
          fontSize: { xs: '1.25rem', sm: '1.5rem' },
        }}
      >
        {name}
      </Typography>
      {description && (
        <Typography
          variant="body2"
          sx={{
            opacity: 0.9,
            maxWidth: { xs: 280, sm: 400 },
            mx: 'auto',
            // Truncate long descriptions on mobile
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          {description}
        </Typography>
      )}
    </Box>
  );
}
